// Mirrors apps/api/modules/onboarding/types/onboarding.ts.

export const PROCESSORS = ["shopify_payments", "stripe", "paypal", "adyen", "nmi", "other"] as const;
export const NETWORKS = ["ethoca", "cdrn", "rdr"] as const;
export const CRMS = ["shopify", "checkoutchamp", "other", "not_sure"] as const;
export const SUBSCRIPTION_APPS = ["none", "loop", "kaching", "skio", "recharge", "checkoutchamp", "other", "not_sure"] as const;
export const HELPDESKS = ["none", "zendesk", "richpanel", "gorgias", "commslayer", "intercom", "gmail", "other", "not_sure"] as const;
export const FULFILMENT_TOOLS = ["shipstation", "shipsidekick", "shipoffers", "jetpack", "ecomflow", "packiyo", "dayone", "oneclick", "shopify", "other", "not_sure"] as const;
export const EMAIL_MARKETING = ["none", "klaviyo", "omnisend", "other", "not_sure"] as const;
export const REFUND_PLATFORMS = ["none", "shopify", "checkoutchamp", "other", "not_sure"] as const;
export const GROUPS = ["billing", "processors", "crm", "refunds", "enrolments", "representment", "golive", "custom"] as const;

export type Processor = typeof PROCESSORS[number];
export type Network = typeof NETWORKS[number];
export type Crm = typeof CRMS[number];
export type SubscriptionApp = typeof SUBSCRIPTION_APPS[number];
export type Helpdesk = typeof HELPDESKS[number];
export type FulfilmentTool = typeof FULFILMENT_TOOLS[number];
export type EmailMarketing = typeof EMAIL_MARKETING[number];
export type RefundPlatform = typeof REFUND_PLATFORMS[number];
export type GroupKey = typeof GROUPS[number];
export type Party = "client" | "network" | "us";
export type ItemNetwork = "chargebackhelp" | "verifi";
export type ItemState = "todo" | "waiting" | "done" | "not_needed";
export type OnboardingKind = "new_client" | "existing_company";
export type OnboardingStage = "new" | "setting_up" | "done";
export type ItemHistoryAction =
  | "asked" | "sent" | "chased" | "done" | "live" | "not_needed" | "retired_by_edit" | "reopened" | "reopened_by_edit" | "back_to_todo"
  | "notes" | "details" | "added";
export type ItemChange = { state: ItemState; fromState: ItemState } | { notes: string } | { details: Record<string, unknown> };

export interface BrandStack {
  crms: Crm[];
  otherCrmName?: string;
  subscriptionApps: SubscriptionApp[];
  otherSubscriptionAppName?: string;
  helpdesks: Helpdesk[];
  otherHelpdeskName?: string;
  fulfilment?: FulfilmentTool[];
  otherFulfilmentName?: string;
  emailMarketing?: EmailMarketing[];
  otherEmailMarketingName?: string;
  refundsIn: RefundPlatform[];
  refundsOtherName?: string;
}

export interface OnboardingBrand {
  brandId?: number;
  name: string;
  storeUrl?: string;
  processors: Processor[];
  otherProcessorNames?: string[];
  isNew: boolean;
  billingProfileId?: number;
  stack: BrandStack;
}

export interface BillingProfileOption {
  billingProfileId: number;
  name: string;
}

export interface OnboardingAnswers {
  companyName: string;
  legalEntity: string;
  signedOn: string; // YYYY-MM-DD
  admin: { firstName: string; lastName: string; email: string };
  brands: OnboardingBrand[];
  alerts: { enabled: boolean; networks: Network[] };
  representment: boolean;
  addOns: string;
  switching: { enabled: boolean; provider?: string; someMidsNew: boolean };
  salesNotes: string;
  commsChannel?: string;
  supportChannel?: string;
  representmentProcessors?: Processor[];
  representmentOtherNames?: string[];
}

export interface ManualRefundsNotice {
  brandName: string;
  platform: string;
  autoRefundsIn: string[];
}

export interface ChecklistNotices {
  paypalOnlyNoAlerts: boolean;
  manualRefunds: ManualRefundsNotice[];
}

export interface OnboardingPreviewItem {
  itemKey: string;
  groupKey: GroupKey;
  party: Party;
  network: ItemNetwork | null;
  brandIndex: number | null;
  params: Record<string, string>;
  brandName: string | null;
  title: string;
  text: string;
  chaseDays: number | null;
}

export interface OnboardingPreview {
  items: OnboardingPreviewItem[];
  notices: ChecklistNotices;
}

export interface OnboardingPrefill {
  companyId: number;
  openOnboardingId: number | null;
  billingProfiles: BillingProfileOption[];
  billingProfileIdByBrand: Record<number, number>;
  answers: Pick<OnboardingAnswers, "companyName" | "brands">
    & Partial<Pick<OnboardingAnswers, "legalEntity" | "admin" | "alerts" | "representment" | "addOns" | "commsChannel" | "supportChannel">>;
}

export interface OnboardingCreated {
  onboardingId: number;
  companyId: number;
}

export type DetailFieldKind = "text" | "multiline" | "email" | "date" | "yesno";

export interface DetailField {
  key: string;
  kind: DetailFieldKind;
}

export interface OnboardingDetailItem {
  itemId: number;
  itemKey: string;
  groupKey: GroupKey;
  party: Party;
  network: ItemNetwork | null;
  brandId: number | null;
  brandName: string | null;
  title: string;
  text: string;
  chaseDays: number | null;
  state: ItemState;
  moves: ItemState[];
  waitingSince: string | null;
  daysWaiting: number | null;
  overdue: boolean;
  notes: string | null;
  detailFields: DetailField[] | null;
  details: Record<string, unknown>;
}

export interface NewOnboardingStep {
  title: string;
  text?: string;
  party: Party;
  network?: ItemNetwork;
}

export interface OnboardingHistoryEntry {
  historyId: number;
  itemId: number;
  actorName: string | null;
  actorFirstName: string | null;
  action: ItemHistoryAction;
  createdAt: string;
}

export interface OnboardingDetail {
  onboardingId: number;
  companyId: number;
  companyName: string;
  kind: OnboardingKind;
  answers: OnboardingAnswers;
  answersVersion: number;
  stage: OnboardingStage;
  signedDays: number | null;
  onboardingDays: number;
  notices: ChecklistNotices;
  createdAt: string;
  createdByEmail: string | null;
  createdByFirstName: string | null;
  newerOnboardingId: number | null;
  items: OnboardingDetailItem[];
  history: OnboardingHistoryEntry[];
}

export interface CompletedOnboardingRow {
  onboardingId: number;
  companyId: number;
  companyName: string;
  finishedOn: string;
  daysTaken: number;
  lastStep: string | null;
  done: number;
  total: number;
}

export interface OnboardingCounts {
  completed: number;
}

export interface OnboardingListRow {
  onboardingId: number;
  companyId: number;
  companyName: string;
  signedDays: number | null;
  onboardingDays: number;
  brandCount: number;
  stage: OnboardingStage;
  done: number;
  total: number;
  waiting: number;
  oldestWaiting: { title: string; days: number } | null;
  overdue: boolean;
}
